/**
 * ReasonPickerDialog - shared confirmation dialog for approve / deny /
 * cancel / waive-fee flows.
 *
 * Reason lists are the tenant's own ``TransferReason`` rows, passed in by
 * the caller. They were a hardcoded frontend constant named after one
 * tenant, so every other tenant saw that tenant's vocabulary and a product
 * owner could not add or rename a reason without a frontend release.
 */

import { useId, useMemo } from 'react'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

export interface ReasonOption {
  code: string
  label: string
}

interface ReasonPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** When empty/undefined, the dialog is a plain confirm. */
  options?: ReasonOption[]
  /**
   * The options are still being fetched.
   *
   * Without this an in-flight fetch looks exactly like "this dialog has no
   * reasons": `options` is empty, the picker is skipped, and a decision that
   * requires a reason is recorded with none. Now the picker shows that it is
   * loading and confirm waits for it.
   */
  optionsLoading?: boolean
  /** When true, the user must pick a reason before confirming. */
  reasonRequired?: boolean
  /** Free-text field (for e.g. fee-waiver notes). */
  requireNote?: boolean
  noteLabel?: string
  noteMinLength?: number
  confirmLabel: string
  confirmVariant?: 'default' | 'destructive'
  pending?: boolean
  onConfirm: (result: { reasonCode?: string; note?: string }) => void
}

export function ReasonPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  options,
  optionsLoading = false,
  reasonRequired = false,
  requireNote = false,
  noteLabel = 'Notes',
  noteMinLength = 0,
  confirmLabel,
  confirmVariant = 'default',
  pending = false,
  onConfirm,
}: ReasonPickerDialogProps) {
  const formName = `reason-picker-${useId()}`
  const hasOptions = !!options && options.length > 0

  // Confirm stays enabled: a missing reason or short note is flagged on the
  // field when the user clicks. A tenant with no reasons configured gets a
  // plain confirm - nothing here they could pick.
  const fields = useMemo<FormField[]>(() => {
    const out: FormField[] = []
    if (hasOptions && !optionsLoading) {
      out.push({
        type: 'select',
        name: 'reason',
        label: 'Reason',
        placeholder: 'Select a reason',
        value: '',
        options: options!.map((o) => ({ value: o.code, label: o.label })),
        ...(reasonRequired ? { required: 'Choose a reason.' } : {}),
      })
    }
    if (requireNote) {
      out.push({
        type: 'textarea',
        name: 'note',
        label: noteLabel,
        value: '',
        placeholder:
          noteMinLength > 0 ? `${noteMinLength}+ characters required` : 'Optional',
        ...(noteMinLength > 0
          ? {
              hint: `At least ${noteMinLength} characters.`,
              validationSchema: z
                .string()
                .trim()
                .min(noteMinLength, `Add at least ${noteMinLength} characters.`),
            }
          : {}),
      })
    }
    return out
  }, [hasOptions, optionsLoading, options, reasonRequired, requireNote, noteLabel, noteMinLength])

  const { Form, ref } = useForm(
    { name: formName, submit: 'footer', fields },
    (values) => {
      onConfirm({
        reasonCode: hasOptions ? String(values.reason || '') || undefined : undefined,
        note: requireNote ? String(values.note || '').trim() : undefined,
      })
    },
    [open, fields],
  )

  const handleOpenChange = (next: boolean) => {
    if (!next) ref.current?.resetForm()
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {optionsLoading && (
          <p
            className="m-0 text-[12px] font-light text-[var(--ink-3)]"
            data-testid="reason-options-loading"
          >
            Loading reasons...
          </p>
        )}
        {/* Stays mounted with no fields: submit goes through it either way. */}
        <Form className={fields.length ? 'flex flex-col gap-4 py-2' : 'hidden'} />

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void ref.current?.submitForm()}
            variant={confirmVariant}
            // In-flight only: the reasons are loading or the decision is sending.
            disabled={pending || optionsLoading}
            data-testid="reason-confirm"
          >
            {pending ? 'Working...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
